import { useContext } from 'react';
import { store } from 'components/Store';
import RegionFilters from 'components/Dashboard/Filters/FiltersData/RegionFilters';
import {
  PartyOptions,
  PositionOptions,
  RoleOptions,
  RoleSearchOptions,
} from 'components/Dashboard/Filters/FiltersData/StakeholdersFilters';
import { ChampionStatusOptions } from 'components/Dashboard/Influence/tableComponents/ChampionStatus';
import { useChangeKeywordsListsOrder } from 'components/Dashboard/hooks/useSearchBarUiFunctions';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import ConfigData from 'config/ConfigData';
import { usStateOptions } from 'components/Dashboard/Filters/FiltersData/LegislationFilters';
import dayjs from 'dayjs';

// Presets for the 'In the last...' condition on the Last interaction date filter.
// Sent to the API as a plain day count (days_ago_lt), not a computed date.
const dateRangePresets = [7, 30, 90, 365].map((days) => ({ name: `${days} days`, days }));

// Drops the month/year from the 'from' date when they match the 'to' date, e.g. "12 and 18 Aug 2026".
const formatDateRange = (from, to) => {
  if (!from || !to) return '';
  const equalYear = dayjs(from).get('year') === dayjs(to).get('year');
  const equalMonth = dayjs(from).get('month') === dayjs(to).get('month');
  const fromFormat = equalYear ? (equalMonth ? 'D' : 'D MMM') : 'D MMM YYYY';
  return `${dayjs(from).format(fromFormat)} and ${dayjs(to).format('D MMM YYYY')}`;
};

const useGroupFiltersData = () => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { keywordsLists, team } = state;
  const { newAgencyPlan, agencyUser } = useTopicOrClientWord();
  const { changeKeywordsListsOrder } = useChangeKeywordsListsOrder({
    keywordsLists: keywordsLists?.filter((item) => item?.id !== null),
  });

  const GroupsFiltersData = {
    categories: [
      {
        name: 'Vocal about...',
        filterName: 'topicMatchedKeywordListIds',
        subtext: `(e.g ${changeKeywordsListsOrder()?.[0]?.name ?? 'immigration'})`,
        type: 'picker',
        placeholder: 'Select a topic',
      },
      {
        name: 'Role',
        filterName: 'categories',
        options: [...RoleOptions, ...RoleSearchOptions],
        type: 'select',
        subtext: ConfigData?.filters?.role?.subtext,
        placeholder: 'Select roles',
      },
      {
        name: 'Party',
        options: PartyOptions,
        filterName: 'party',
        type: 'select',
        subtext: ConfigData?.filters?.party?.subtext,
        placeholder: 'Select parties',
      },
      {
        name: 'State',
        filterName: 'constituency',
        options: usStateOptions.filter((item) => item.name !== 'US Congress'),
        type: 'select',
        subtext: '(e.g. California, New York)',
        placeholder: 'Select states',
        hidden: ConfigData?.filters?.state?.configHidden,
      },
      {
        name: 'Position',
        filterName: 'positionCategories',
        options: PositionOptions,
        type: 'select',
        subtext: '(e.g. Cabinet, Shadow Cabinet)',
        placeholder: 'Select positions',
        hidden: ConfigData?.filters?.position?.configHidden,
      },
      {
        name: 'Department',
        type: 'membership',
        subtype: 'DepartmentSelectType',
        subtext: '(e.g. Department for Education)',
        filterName: 'departments',
        hidden: ConfigData?.filters?.department?.configHidden,
      },
      {
        name: 'Nation',
        filterName: 'categories',
        type: 'select',
        subtext: '(e.g. UK, Wales, Scotland)',
        placeholder: 'Select nations',
        options: [
          {
            name: 'Westminster',
            filterValue: ['MP', 'Lord', 'Special Adviser'],
            labelClassName: 'label-country-sort uk-flag',
          },
          {
            name: 'Senedd',
            filterValue: 'MS',
            labelClassName: 'label-country-sort label-wales',
          },
          {
            name: 'Holyrood',
            filterValue: 'MSP',
            labelClassName: 'label-country-sort label-scotland',
          },
          {
            name: 'Stormont',
            filterValue: 'MLA',
            labelClassName: 'label-country-sort label-northernireland',
          },
        ],
        hidden: ConfigData?.filters?.nation?.hidden,
      },
      {
        name: 'Last interaction date',
        filterName: 'lastInteractionDate',
        type: 'dateRange',
        subtext: '(e.g. in the last 7 days)',
      },
      {
        name: 'Champion status',
        filterName: 'championStatus',
        options: ChampionStatusOptions?.filter((item) => item?.name !== 'No status'),
        type: 'select',
        subtext: '(e.g. promoter, detractor)',
        placeholder: 'Select champion status',
      },
      {
        name: 'Sentiment',
        filterName: 'topicMatchedKeywordListIds',
        subtext: `(e.g positive about ${changeKeywordsListsOrder()?.[0]?.name ?? 'immigration'})`,
        type: 'selectSentiment',
        placeholder: 'Select a topic',
        options: [{ name: 'Positive' }, { name: 'Negative' }, { name: 'Mixed' }, { name: 'Neutral' }],
      },
      {
        name: 'Tags',
        type: 'tags',
        filterName: 'tags',
      },
      {
        name: 'Region',
        filterName: 'region',
        options: RegionFilters?.[0]?.options,
        type: 'select',
        placeholder: 'Select regions',
        hidden: ConfigData?.filters?.region?.configHidden,
      },
      {
        name: 'Select Committee',
        type: 'membership',
        subtype: 'MembershipOfSelectCommittees',
        filterName: 'selectCommittees',
        hidden: ConfigData?.filters?.selectCommittees?.configHidden,
      },
      {
        name: 'Lords Select Committee',
        type: 'membership',
        subtype: 'MembershipOfLordsSelectCommittees',
        filterName: 'lordsSelectCommittees',
        hidden: ConfigData?.filters?.lordsSelectCommittees?.configHidden,
      },
      {
        name: 'APPG (officers)',
        type: 'membership',
        subtype: 'MembershipOfAPPGs',
        filterName: 'allPartyGroups',
        hidden: ConfigData?.filters?.allPartyGroups?.configHidden,
      },
    ],
    operators: [
      /* { name: 'is equal to', value: 'str_eq', type: 'single' },
      { name: 'is not equal to', value: 'str_not_eq', type: 'single' }, */
      {
        name: 'is equal to any of',
        format: () => <>is equal to any of</>,
        value: 'str_eq',
        type: 'multiselect',
      },
      {
        name: 'is not equal to any of',
        format: () => <>is not equal to any of</>,
        value: 'str_not_eq',
        type: 'multiselect',
      },
      {
        name: 'is known',
        format: () => <>is known</>,
        value: 'exists',
        type: 'not_value_filter',
      },
      {
        name: 'is not known',
        format: () => <>is not known</>,
        value: 'not_exists',
        type: 'not_value_filter',
      },
      {
        name: 'In the last',
        format: () => <>in the last</>,
        value: 'days_ago_lt',
        type: 'date_preset_input',
        categoryTypes: ['dateRange'],
      },
      {
        name: 'More than',
        format: () => <>more than</>,
        value: 'days_ago_gt',
        type: 'date_preset_input',
        categoryTypes: ['dateRange'],
      },
      {
        name: 'After',
        format: () => <>after</>,
        value: 'date_gte',
        type: 'date_input',
        categoryTypes: ['dateRange'],
      },
      {
        name: 'Before',
        format: () => <>before</>,
        value: 'date_lt',
        type: 'date_input',
        categoryTypes: ['dateRange'],
      },
      {
        name: 'Between',
        format: () => <>between</>,
        value: 'date_between',
        type: 'date_range_input',
        categoryTypes: ['dateRange'],
      },
    ],
    topicDates: [
      {
        name: '365 days',
        filterValue: '12mo',
      },
      {
        name: '90 days',
        filterValue: '3mo',
      },
      {
        name: '30 days',
        filterValue: '1mo',
      },
      /* {
        name: '7 days',
        filterValue: '1w',
      }, */
    ],
  };

  const getPropByName = (key, value) => {
    return GroupsFiltersData[key].find((item) => item.name === value);
  };

  const MPsFilter = {
    category: getPropByName('categories', 'Role'),
    operator: getPropByName('operators', 'is equal to any of'),
    sentimentTopic: null,
    value: [
      {
        name: 'MPs',
        filterValue: 'MP',
        hash: 'mps',
      },
    ],
  };

  const NationFilter = {
    category: getPropByName('categories', 'Nation'),
    operator: getPropByName('operators', 'is equal to any of'),
    sentimentTopic: null,
    topicDate: getPropByName('topicDates', '365 days'),
    value: [getPropByName('categories', 'Nation').options[0]],
    expandedFilter: true,
  };

  const groupsPreselectedFiltersData = [
    {
      label: 'All MPs',
      plainDescription: 'Shows all MPs in the current Parliament',
      description: (
        <span>
          Shows all MPs in the <span className='nowrap-item'>current Parliament</span>
        </span>
      ),
      filters: [MPsFilter],
    },
    {
      label: 'Vocal about...',
      description: 'Shows policymakers who are vocal about any topic',
      filters: [
        {
          category: getPropByName('categories', 'Vocal about...'),
          operator: getPropByName('operators', 'is equal to any of'),
          sentimentTopic: null,
          topicDate: getPropByName('topicDates', '365 days'),
          expandedFilter: true,
        },
        NationFilter,
      ],
    },
    {
      label: `${agencyUser || newAgencyPlan ? `${team?.expires ? 'Prospect' : 'Client'}` : 'Our'} champions`,
      description: `Shows all policymakers you have manually tagged as ‘Promoters’ ${
        agencyUser || newAgencyPlan ? `for this ${team?.expires ? 'prospect' : 'client'}` : ''
      }`,
      type: 'champions',
      filters: [
        {
          category: getPropByName('categories', 'Champion status'),
          operator: getPropByName('operators', 'is equal to any of'),
          sentimentTopic: null,
          value: {
            name: 'Promoter',
            color: '#d9ead3',
          },
        },
      ],
    },
    {
      label: 'Positive about...',
      description: 'Shows policymakers who have positive sentiment in relation to any topic',
      filters: [
        {
          category: getPropByName('categories', 'Sentiment'),
          operator: getPropByName('operators', 'is equal to any of'),
          topicDate: getPropByName('topicDates', '365 days'),
          value: {
            name: 'Positive',
          },
          expandedFilter: true,
        },
        NationFilter,
      ],
    },
    {
      label: 'Negative about...',
      description: 'Shows policymakers who have negative sentiment in relation to any topic',
      filters: [
        {
          category: getPropByName('categories', 'Sentiment'),
          operator: getPropByName('operators', 'is equal to any of'),
          topicDate: getPropByName('topicDates', '365 days'),
          value: {
            name: 'Negative',
          },
          expandedFilter: true,
        },
        NationFilter,
      ],
    },
    {
      label: 'Labour MPs',
      description: (
        <span>
          Shows all <span className='nowrap-item'>Labour MPs</span> in{' '}
          <span className='nowrap-item'>the current Parliament</span>
        </span>
      ),
      plainDescription: 'Shows all current Labour MPs',
      filters: [
        MPsFilter,
        {
          category: getPropByName('categories', 'Party'),
          operator: getPropByName('operators', 'is equal to any of'),
          sentimentTopic: null,
          value: [
            {
              name: 'Labour',
            },
          ],
        },
      ],
    },
    {
      label: 'Conservative MPs',
      description: (
        <span>
          Shows all <span className='nowrap-item'>Conservative MPs</span>{' '}
          <span className='nowrap-item'>in the current Parliament</span>
        </span>
      ),
      plainDescription: 'Shows all current Conservative MPs',
      filters: [
        MPsFilter,
        {
          category: getPropByName('categories', 'Party'),
          operator: getPropByName('operators', 'is equal to any of'),
          sentimentTopic: null,
          value: [
            {
              name: 'Conservative',
            },
          ],
        },
      ],
    },
    {
      label: 'All MSPs',
      description: 'Shows all current Members of the Scottish Parliament',
      filters: [
        {
          category: getPropByName('categories', 'Role'),
          operator: getPropByName('operators', 'is equal to any of'),
          sentimentTopic: null,
          value: [
            {
              name: 'MSPs',
              filterValue: 'MSP',
              hash: 'msp',
              optionConditionToShow: [
                {
                  keywordLists: ['all'],
                  locations: ['/stakeholder-mapping', '/policymakers'],
                },
                {
                  locations: ['/search-results/stakeholders', '/people/directory'],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      label: 'All MSs',
      description: 'Shows all Members of the Senedd in the Welsh Parliament',
      filters: [
        {
          category: getPropByName('categories', 'Role'),
          operator: getPropByName('operators', 'is equal to any of'),
          sentimentTopic: null,
          value: [
            {
              name: 'MSs',
              filterValue: 'MS',
              hash: 'mss',
              optionConditionToShow: [
                {
                  keywordLists: ['all'],
                  locations: ['/stakeholder-mapping', '/policymakers'],
                },
                {
                  locations: ['/search-results/stakeholders', '/people/directory'],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      label: 'All MLAs',
      description: 'Shows all current Members of the Northern Ireland Assembly',
      filters: [
        {
          category: getPropByName('categories', 'Role'),
          operator: getPropByName('operators', 'is equal to any of'),
          sentimentTopic: null,
          value: [
            {
              name: 'MLAs',
              filterValue: 'MLA',
              hash: 'mla',
              optionConditionToShow: [
                {
                  keywordLists: ['all'],
                  locations: ['/stakeholder-mapping', '/policymakers'],
                },
                {
                  locations: ['/search-results/stakeholders', '/people/directory'],
                },
              ],
            },
          ],
        },
      ],
    },
  ];
  let categoriesWithRequiredTopicInput = ['Vocal about...', 'Sentiment'];

  return {
    GroupsFiltersData,
    groupsPreselectedFiltersData,
    categoriesWithRequiredTopicInput,
    dateRangePresets,
    formatDateRange,
  };
};

export default useGroupFiltersData;
